import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as Util from 'biojs/Util';
import {Sequence} from 'components/Sequence';
import * as AssemblyConstants from 'constants/AssemblyConstants';
import {Cutsite as CutsiteType} from 'constants/MolbioConstants';
import Cutsite from 'molbio/enzymes/backbone/CutSite';
import {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import PopoverIcon from 'ui-library/PopoverIcon';
import {InputChangeEvent, Optional} from 'util/Types';

import {Assembly, AssemblySlotType, AssemblyWizardFragmentSequenceMetadata} from './AssemblyModels';
// @ts-expect-error
import * as AssemblyUtil from './AssemblyUtil';
import ShowFragmentComponent from './ShowFragmentComponent';
import SpacerEditor from './SpacerEditor';

const {formatBaseCount} = Util;
const {ASSEMBLY_TYPE} = AssemblyConstants;
const {getSitesInsideFragmentWarning, siteInsideFragment} = AssemblyUtil;

const SET_FROM_SELECTION = 'selection';
const SET_FROM_CUT_SITE = 'cut-sites';

interface FragmentActionsProps {
  activeSlot: AssemblySlotType;
  enzyme?: Enzyme;
  readEnzymeFromSelection: boolean;
  selectedStartSite?: Optional<number>;
  selectedEndSite?: Optional<number>;
  selection?: {
    start: number;
    end: number;
    cutStart: Cutsite;
    cutEnd: Cutsite;
  };
  setFrom: string;
  sequence: Sequence;
}

class FragmentActions extends React.Component<FragmentActionsProps> {
  setFragment = (): void => {
    let end: number;
    let endEnzymeID;
    let reverse;
    let start: number;
    let startEnzymeID;
    const selection = this.props.selection!;
    if (this.props.setFrom === SET_FROM_CUT_SITE) {
      start = this.props.selectedStartSite!;
      end = this.props.selectedEndSite!;
      startEnzymeID = this.props.enzyme!.id;
      endEnzymeID = this.props.enzyme!.id;
      if (end > start) {
        reverse = false;
      } else if (this.props.sequence.circular()) {
        reverse = false;
      } else {
        // Force reverse if linear and end <= start.
        reverse = true;
        [start, end] = Array.from([end, start]);
      }
    } else {
      ({start} = selection);
      ({end} = selection);
      if (this.props.readEnzymeFromSelection && selection.cutStart != null && selection.cutEnd != null) {
        startEnzymeID = selection.cutStart.get('enzyme').id;
        endEnzymeID = selection.cutEnd.get('enzyme').id;
      } else {
        startEnzymeID = null;
        endEnzymeID = null;
      }
      reverse = false;
    }
    this.props.activeSlot
      .setSingleFragment({
        sequenceID: this.props.sequence.id,
        sequenceMetadata: {name: this.props.sequence.name()} as AssemblyWizardFragmentSequenceMetadata,
        name: this.props.sequence.name(),
        start,
        end: sequtil.mod(end, this.props.sequence.length()),
        startEnzymeID,
        endEnzymeID,
        reverse,
      })
      .fail(notifications.notifyError);
  };

  willUseEnzymes = (): boolean => {
    if (this.props.setFrom === SET_FROM_CUT_SITE) {
      return true;
    }
    if (!this.props.readEnzymeFromSelection) {
      return false;
    }
    return (
      (this.props.selection != null ? this.props.selection.cutStart : undefined) != null &&
      (this.props.selection != null ? this.props.selection.cutEnd : undefined) != null
    );
  };

  override render(): JSX.Element {
    const enableSet = (() => {
      switch (this.props.setFrom) {
        case SET_FROM_SELECTION:
          return (
            (this.props.selection != null ? this.props.selection.start : undefined) != null &&
            (this.props.selection != null ? this.props.selection.end : undefined) != null
          );
        case SET_FROM_CUT_SITE:
          return this.props.selectedStartSite != null && this.props.selectedEndSite != null;
        default:
          return true;
      }
    })();
    return (
      <div className="text-center">
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.SMALL}
          className="u-tiny-spacing-bottom"
          disabled={!enableSet}
          onClick={this.setFragment}
          text="Set Fragment"
          dataTestAttrs={{component: 'CurrentFragmentInfo', element: 'setFragmentButton'}}
        />
        <div className="faded">{this.willUseEnzymes() ? 'Use existing sites.' : 'Design a primer pair.'}</div>
      </div>
    );
  }
}

interface GoldenGateSetFragmentComponentState {
  setFrom: string;
  selectedStartSite: Optional<number>;
  selectedEndSite: Optional<number>;
}

class GoldenGateSetFragmentComponent extends React.Component<
  CurrentFragmentInfoProps,
  GoldenGateSetFragmentComponentState
> {
  constructor(props: CurrentFragmentInfoProps) {
    super(props);
    const [start, end] = Array.from(this._sitesFromSelection(props));

    this.state = {
      setFrom: SET_FROM_SELECTION,
      selectedStartSite: start,
      selectedEndSite: end,
    };
  }

  _sitesFromSelection = (props: CurrentFragmentInfoProps): [Optional<number>, Optional<number>] => {
    let end;
    let start;
    const selectionStart = props.selection != null ? props.selection.start : undefined;
    const selectionEnd = props.selection != null ? props.selection.end : undefined;
    // eslint-disable-next-line no-restricted-syntax
    const validPositions = _.pluck(props.cutsites, 'position');
    if (Array.from(validPositions).includes(selectionStart)) {
      start = selectionStart;
    } else {
      start = null;
    }
    if (Array.from(validPositions).includes(selectionEnd)) {
      end = selectionEnd;
    } else {
      end = null;
    }
    return [start, end];
  };

  _setDefaultCuts = (
    sequence: Sequence,
    enzyme: Enzyme,
    cutsites: Array<CutsiteType>,
    selectedStartSite: Optional<number>,
    selectedEndSite: Optional<number>
  ): void => {
    if (selectedStartSite == null) {
      // eslint-disable-next-line no-restricted-syntax
      const startCut = _.find(
        cutsites,
        (site) =>
          site.position !== selectedEndSite &&
          !siteInsideFragment(sequence, enzyme, {start: site.position, cutsites})
      );
      if (startCut != null) {
        selectedStartSite = startCut.position;
        this.setStartSite(selectedStartSite);
      }
    }

    if (selectedEndSite == null) {
      // eslint-disable-next-line no-restricted-syntax
      const endCut = _.find(
        cutsites,
        (site) =>
          site.position !== selectedStartSite &&
          !siteInsideFragment(sequence, enzyme, {end: site.position, cutsites})
      );
      if (endCut != null) {
        selectedEndSite = endCut.position;
        this.setEndSite(selectedEndSite);
      }
    }

    if (cutsites.length === 2) {
      this.setState({setFrom: SET_FROM_CUT_SITE});
    }
  };

  override UNSAFE_componentWillMount(): void {
    this._setDefaultCuts(
      this.props.sequence,
      this.props.enzyme,
      this.props.cutsites,
      this.state.selectedStartSite,
      this.state.selectedEndSite
    );
  }

  override UNSAFE_componentWillReceiveProps(nextProps: CurrentFragmentInfoProps): void {
    let nextStartSite: Optional<number> = __guard__(
      _.findWhere(nextProps.cutsites, {position: this.state.selectedStartSite}),
      (x) => x.position
    );
    let nextEndSite: Optional<number> = __guard__(
      _.findWhere(nextProps.cutsites, {position: this.state.selectedEndSite}),
      (x1) => x1.position
    );
    let setStartFromCut = false;
    let setEndFromCut = false;
    // Clear away if enzyme changes.
    if (this.props.enzyme.id !== nextProps.enzyme.id) {
      this.setState({
        selectedStartSite: null,
        selectedEndSite: null,
      });
      nextStartSite = null;
      nextEndSite = null;
    }
    // Clear away if cut sites change.
    if (this.state.selectedStartSite != null && nextStartSite == null) {
      this.setState({selectedStartSite: null});
      nextStartSite = null;
    }
    if (this.state.selectedEndSite != null && nextEndSite == null) {
      this.setState({selectedEndSite: null});
      nextEndSite = null;
    }
    // Set cut sites if selection matches.
    const [nextSelectionStart, nextSelectionEnd] = Array.from(this._sitesFromSelection(nextProps));
    if (nextSelectionStart != null) {
      this.setStartSite(nextSelectionStart);
      setStartFromCut = true;
      nextStartSite = nextSelectionStart;
    }
    if (nextSelectionEnd != null) {
      this.setEndSite(nextSelectionEnd);
      setEndFromCut = true;
      nextEndSite = nextSelectionEnd;
    }

    if (setStartFromCut && setEndFromCut) {
      // If both are set from cut, use SET_FROM_CUT_SITE
      this.setState({setFrom: SET_FROM_CUT_SITE});
      return;
    }

    if (this.props.sequence !== nextProps.sequence || this.props.enzyme.id !== nextProps.enzyme.id) {
      this._setDefaultCuts(
        nextProps.sequence,
        nextProps.enzyme,
        nextProps.cutsites,
        nextStartSite,
        nextEndSite
      );
    }

    // If selection is changing, set back to using it.
    const startChanged =
      (this.props.selection != null ? this.props.selection.start : undefined) !==
      (nextProps.selection != null ? nextProps.selection.start : undefined);
    const endChanged =
      (this.props.selection != null ? this.props.selection.end : undefined) !==
      (nextProps.selection != null ? nextProps.selection.end : undefined);
    if (startChanged || endChanged) {
      this.setState({setFrom: SET_FROM_SELECTION});
    }
  }

  setSetFrom = (e: InputChangeEvent): void => {
    this.setState({setFrom: e.target.value});
  };

  setStartSite = (site: number): void => {
    this.setState({selectedStartSite: site});
    if (site === this.state.selectedEndSite) {
      this.setState({selectedEndSite: null});
    }
  };

  setEndSite = (site: number): void => {
    this.setState({selectedEndSite: site});
    if (site === this.state.selectedStartSite) {
      this.setState({selectedStartSite: null});
    }
  };

  handleStartSiteChange = (site: number): void => {
    this.props.sequence.trigger('jump', site);
    this.setStartSite(site);
  };

  handleEndSiteChange = (site: number): void => {
    this.props.sequence.trigger('jump', site);
    this.setEndSite(site);
  };

  renderSitesInsideFragmentWarning = (): JSX.Element | null => {
    if (this.props.sequence == null || this.state.setFrom !== SET_FROM_CUT_SITE) {
      return null;
    }
    const sitesInsideFragmentWarning = getSitesInsideFragmentWarning(this.props.sequence, this.props.enzyme, {
      start: this.state.selectedStartSite,
      end: this.state.selectedEndSite,
      cutsites: this.props.cutsites,
    });
    if (sitesInsideFragmentWarning == null) {
      return null;
    }
    return (
      <span className="u-warning-color">
        <PopoverIcon icon="warning" renderContent={() => sitesInsideFragmentWarning} />
      </span>
    );
  };

  override render(): JSX.Element {
    // Need at least two cutsites.
    const notEnoughCutsites = this.props.cutsites.length < 2;
    const startIndex = this.props.sequence != null ? this.props.sequence.startIndex() : undefined;
    return (
      <div className="u-small-spacing-top">
        <div className="pull-right">
          <FragmentActions
            activeSlot={this.props.activeSlot}
            enzyme={this.props.enzyme}
            readEnzymeFromSelection={false}
            selectedStartSite={this.state.selectedStartSite}
            selectedEndSite={this.state.selectedEndSite}
            selection={this.props.selection}
            sequence={this.props.sequence}
            setFrom={this.state.setFrom}
          />
        </div>
        <div>
          <label className="radio-inline">
            <input
              type="radio"
              name="set-from-option"
              value={SET_FROM_SELECTION}
              checked={this.state.setFrom === SET_FROM_SELECTION}
              onChange={this.setSetFrom}
            />{' '}
            <div>
              <div className="u-tiny-spacing-bottom">Selection</div>
              <div className={classNames('faded', css(styles.assemblyFragmentSelection))}>
                {(() => {
                  if (this.props.selection == null) {
                    return 'None';
                  } else if (this.props.selection.start != null && this.props.selection.end == null) {
                    return `${sequtil.modelStartToDisplay(this.props.selection.start, startIndex)} to -`;
                  } else {
                    assert(
                      this.props.selection.start != null && this.props.selection.end != null,
                      'expected selection to have start and end'
                    );
                    return `${sequtil.modelStartToDisplay(this.props.selection.start, startIndex)} to \
      ${sequtil.modelEndToDisplay(this.props.selection.end, this.props.sequence.length(), startIndex)}`;
                  }
                })()}
              </div>
            </div>
          </label>
          <label className="radio-inline">
            <input
              type="radio"
              name="set-from-option"
              value={SET_FROM_CUT_SITE}
              checked={this.state.setFrom === SET_FROM_CUT_SITE}
              onChange={this.setSetFrom}
            />{' '}
            <div>
              <div className="u-tiny-spacing-bottom">
                <a onClick={this.props.toggleParameters}>{this.props.assembly.enzyme().get('name')}</a>
                {' Cut Sites '}
                {this.renderSitesInsideFragmentWarning()}
              </div>
              {(() => {
                if (notEnoughCutsites) {
                  return (
                    <div className={classNames('faded', css(styles.assemblyFragmentSelection))}>
                      {this.props.cutsites.length === 0 ? 'No Cut Sites' : 'Requires 2 Cut Sites'}
                    </div>
                  );
                } else {
                  let endText;
                  let startText;
                  this.props.sequence.length();
                  if (this.state.selectedStartSite != null) {
                    startText = sequtil.modelStartToDisplay(this.state.selectedStartSite, startIndex);
                  } else {
                    startText = 'Start';
                  }
                  if (this.state.selectedEndSite != null) {
                    endText = sequtil.modelStartToDisplay(this.state.selectedEndSite, startIndex);
                  } else {
                    endText = 'End';
                  }

                  return (
                    <div>
                      <Dropdown
                        menu={
                          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.SMALL}>
                            {this.props.cutsites.map((cutsite, i) => (
                              <Option
                                key={i}
                                onSelect={() => {
                                  this.handleStartSiteChange(cutsite.position);
                                }}
                              >
                                {sequtil.modelStartToDisplay(cutsite.position, startIndex)}
                              </Option>
                            ))}
                          </Menu>
                        }
                      >
                        <Button
                          isDropdown={true}
                          buttonStyle={ButtonStyle.TERTIARY_GRAY}
                          size={ButtonSize.SMALL}
                          text={startText}
                        />
                      </Dropdown>
                      {' to '}
                      <Dropdown
                        menu={
                          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.SMALL}>
                            {this.props.cutsites.map((cutsite, i) => (
                              <Option
                                key={i}
                                onSelect={() => {
                                  this.handleEndSiteChange(cutsite.position);
                                }}
                              >
                                {sequtil.modelStartToDisplay(cutsite.position, startIndex)}
                              </Option>
                            ))}
                          </Menu>
                        }
                      >
                        <Button
                          isDropdown={true}
                          buttonStyle={ButtonStyle.TERTIARY_GRAY}
                          size={ButtonSize.SMALL}
                          text={endText}
                        />
                      </Dropdown>
                    </div>
                  );
                }
              })()}
            </div>
          </label>
        </div>
      </div>
    );
  }
}

class GibsonSetFragmentComponent extends React.Component<CurrentFragmentInfoProps> {
  override render(): JSX.Element {
    const readEnzymeFromSelection = this.props.activeSlot.index() === 0;
    const {selection} = this.props;
    return (
      <div className="u-small-spacing-top">
        <div className="pull-right">
          <FragmentActions
            activeSlot={this.props.activeSlot}
            readEnzymeFromSelection={readEnzymeFromSelection}
            selection={this.props.selection}
            sequence={this.props.sequence}
            setFrom={SET_FROM_SELECTION}
          />
        </div>
        <div className="faded">
          {(() => {
            if (selection == null || selection.start == null || selection.end == null) {
              return 'Select bases on a sequence to set this fragment.';
            } else {
              const startIndex = this.props.sequence.startIndex();
              const seqLength = this.props.sequence.length();
              const selectionLength = sequtil.length(selection.start, selection.end, seqLength);
              return [
                <div key="selection-info">
                  {'Selected '}
                  <strong>{formatBaseCount(selectionLength)}</strong>
                  {' from '}
                  <strong>{sequtil.modelStartToDisplay(selection.start, startIndex)}</strong>
                  {' to '}
                  <strong>{sequtil.modelEndToDisplay(selection.end, seqLength, startIndex)}</strong>.
                </div>,
                (() => {
                  if (readEnzymeFromSelection && selection.cutStart != null && selection.cutEnd != null) {
                    const {cutStart} = selection;
                    const {cutEnd} = selection;
                    return (
                      <div key="enzyme-info">
                        {'Using sticky ends cut by '}
                        <span style={{backgroundColor: cutStart.color() || undefined, color: 'black'}}>
                          {cutStart.name()}
                        </span>
                        {' and '}
                        <span style={{backgroundColor: cutEnd.color() || undefined, color: 'black'}}>
                          {cutEnd.name()}
                        </span>
                        .
                      </div>
                    );
                  }
                  return null;
                })(),
              ];
            }
          })()}
        </div>
      </div>
    );
  }
}

interface CurrentFragmentInfoProps {
  activeSlot: AssemblySlotType;
  assembly: Assembly;
  cutsites: Array<CutsiteType>;
  enzyme: Enzyme;
  selection?: {
    start: number;
    end: number;
    cutStart: Cutsite;
    cutEnd: Cutsite;
  };
  sequence: Sequence;
  toggleParameters: () => void;
}

function CurrentFragmentInfo(props: CurrentFragmentInfoProps): JSX.Element {
  if (props.assembly.method() === ASSEMBLY_TYPE.DIGEST) {
    // Digest has its own display.
    return <div />;
  }
  return (
    <div
      className={classNames({
        'current-fragment-info': true,
        'u-small-spacing-top': props.activeSlot.isEmpty(),
      })}
    >
      <h4>
        {props.activeSlot.isSpacer()
          ? 'Edit Spacer'
          : (props.activeSlot != null ? props.activeSlot.isEmpty() : undefined)
            ? 'Set Fragment'
            : 'Current Fragment: Assembled Preview'}
      </h4>
      {(() => {
        if (props.activeSlot.isSpacer()) {
          return <SpacerEditor slot={props.activeSlot} />;
        } else if (
          props.activeSlot.isEmpty() ||
          props.activeSlot.singleFragment().getSequence()!.length() === 0
        ) {
          switch (props.assembly.method()) {
            case ASSEMBLY_TYPE.GOLDEN_GATE:
              return <GoldenGateSetFragmentComponent {...props} />;
            case ASSEMBLY_TYPE.GIBSON:
              return <GibsonSetFragmentComponent {...props} />;
            default:
              throw new Error(`Unexpected assembly type: ${props.assembly.method()}`);
          }
        } else {
          return (
            <ShowFragmentComponent
              activeSlot={props.activeSlot}
              assembly={props.assembly}
              cutsites={props.cutsites}
            />
          );
        }
      })()}
    </div>
  );
}

export default CurrentFragmentInfo;

function __guard__<T, V>(value: Optional<T>, transform: (arg: T) => V): V | undefined {
  return value != null ? transform(value) : undefined;
}

const styles = StyleSheet.create({
  assemblyFragmentSelection: {
    paddingTop: '3px',
    paddingBottom: '4px',
  },
});
